import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { Absorber, InsertAbsorberBody, UpdateAbsorberBody } from "./types/absorbers";

export const useAbsorbersApi = () => {
  const api = useApi();

  const useAdminSteelVersionAbsorbers = useCallback(
    (steelVersionId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/steel_versions/${steelVersionId}/absorbers`, query);
      return useSWR<Paginated<Absorber>, CustomError, string>(url, (url) =>
        api.get<Paginated<Absorber>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminAbsorber = useCallback(
    async (body: InsertAbsorberBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/absorbers`;
      const id = await api.post<{ id: string }, InsertAbsorberBody>(url, body);
      mutateCache([url, `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/absorbers`]);
      return id;
    },
    [api],
  );

  const updateAdminAbsorber = useCallback(
    async (absorberId: string, body: UpdateAbsorberBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/absorbers/${absorberId}`;
      await api.put<UpdateAbsorberBody>(url, body);
      mutateCache([
        `/admin/settings/vertigrip/absorbers`,
        `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/absorbers`,
      ]);
    },
    [api],
  );

  const updateAdminAbsorberVisibility = useCallback(
    async (absorberId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/absorbers/${absorberId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        `/admin/settings/vertigrip/absorbers`,
        `/admin/settings/vertigrip/steel_versions/${absorberId}/absorbers`,
      ]);
    },
    [api],
  );

  const updateAdminAbsorberOrder = useCallback(
    async (absorberId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/absorbers/${absorberId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        `/admin/settings/vertigrip/absorbers`,
        `/admin/settings/vertigrip/steel_versions/${absorberId}/absorbers`,
      ]);
    },
    [api],
  );

  const deleteAdminAbsorber = useCallback(
    async (absorberId: string, steelVersionId: string): Promise<void> => {
      await api.del(`/admin/settings/vertigrip/absorbers/${absorberId}`);
      mutateCache([
        `/admin/settings/vertigrip/absorbers`,
        `/admin/settings/vertigrip/absorbers/${absorberId}`,
        `/admin/settings/vertigrip/steel_versions/${steelVersionId}/absorbers`,
        `/admin/settings/vertigrip/steel_versions/${steelVersionId}/absorbers/${absorberId}`,
      ]);
      mutateCache([`/admin/settings/vertigrip/steel_versions/${steelVersionId}/absorbers`]);
    },
    [api],
  );

  return {
    useAdminSteelVersionAbsorbers,
    insertAdminAbsorber,
    updateAdminAbsorber,
    updateAdminAbsorberVisibility,
    updateAdminAbsorberOrder,
    deleteAdminAbsorber,
  };
};
